import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import { PORTFOLIO_IDENTIFIER_COLUMNS, normalizeIdentifierHeader } from '../config/UploadConstant';

const text = (value) => String(value ?? '').normalize('NFC').trim().replace(/\s+/gu, ' ');
// Excel's own limit for a number format code.
const MAX_NUMBER_FORMAT_CHARS = 255;

/**
 * The first worksheet of an Excel file, reading each whole number in a
 * zero-padded number format (such as 000000000) as the text Excel shows,
 * leading zeros included. That format is how a workbook keeps an identifier's
 * zeros in a number cell. The linked-Excel reader on the server applies the
 * same rule, so both imports read the same values.
 */
export function readFirstWorksheet(buffer) {
    const book = XLSX.read(buffer, { type: 'array', cellNF: true });
    const sheet = book.Sheets[book.SheetNames[0]];
    Object.keys(sheet).forEach((reference) => {
        const cell = sheet[reference];
        const format = typeof cell?.z === 'string' ? cell.z : '';
        if (
            reference.startsWith('!')
            || cell.t !== 'n'
            || format.length > MAX_NUMBER_FORMAT_CHARS
            || !/^0{2,}$/.test(format)
            || !Number.isSafeInteger(cell.v)
            || cell.v < 0
        ) return;
        const shown = String(cell.v).padStart(format.length, '0');
        sheet[reference] = { ...cell, t: 's', v: shown, w: shown };
    });
    return sheet;
}

export async function readImportMatrix(file) {
    const extension = file.name.split('.').pop().toLowerCase();
    if (!['csv', 'xlsx', 'xls'].includes(extension)) throw new Error('Upload a valid CSV or Excel file.');
    const buffer = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => resolve(event.target.result);
        reader.onerror = () => reject(new Error('The file could not be read. Try choosing it again.'));
        reader.readAsArrayBuffer(file);
    });
    if (extension === 'csv') {
        const parsed = Papa.parse(new TextDecoder('utf-8').decode(buffer), { header: false, skipEmptyLines: false });
        if (parsed.errors.some((error) => error.code !== 'UndetectableDelimiter')) {
            throw new Error('CSV parse error. Please check file format.');
        }
        return parsed.data;
    }
    return XLSX.utils.sheet_to_json(readFirstWorksheet(buffer), { header: 1, defval: '', blankrows: true });
}

// The Actual Portfolio column check. A Watchlist import's column check comes
// from the server; see watchlistColumnReview.
export function inspectPortfolioColumns(matrix) {
    const headerIndex = matrix.findIndex((row) => row.some((value) => text(value)));
    if (headerIndex < 0) throw new Error('This file is empty.');
    // Include cells beyond a short header row so no populated column disappears
    // from the user's confirmation without an explicit exclusion reason.
    const width = matrix.reduce((maximum, row) => Math.max(maximum, row.length), 0);
    const headers = Array.from({ length: width }, (_, index) => normalizeIdentifierHeader(matrix[headerIndex][index]));
    const identifierChoices = headers.filter((header) => PORTFOLIO_IDENTIFIER_COLUMNS.includes(header));
    const quantityChoices = headers.filter((header) => ['quantity', 'shares', 'qty'].includes(header));
    const columns = headers.map((name, index) => {
        const values = matrix.slice(headerIndex + 1).map((row) => text(row[index])).filter(Boolean);
        const column = { index, name, label: text(matrix[headerIndex][index]) || `Unnamed column ${index + 1}`, examples: [...new Set(values)].slice(0, 3), selectable: true, checked: true, reason: '' };
        if (!name || headers.indexOf(name) !== index) {
            return { ...column, selectable: false, checked: false, role: 'Excluded', reason: name ? 'Duplicate column name. Rename it to import it separately.' : 'A column name is required.' };
        }
        if (PORTFOLIO_IDENTIFIER_COLUMNS.includes(name)) return { ...column, role: 'Identifier' };
        if (name === 'country') return { ...column, role: 'Listing context' };
        if (quantityChoices.includes(name)) return { ...column, role: 'Share quantity' };
        return { ...column, role: 'Excluded', selectable: false, checked: false, reason: 'Actual Portfolio imports do not process this column.' };
    });
    return { matrix, headerIndex, columns, destination: 'portfolio', identifierChoices: [...new Set(identifierChoices)], quantityChoices: [...new Set(quantityChoices)] };
}

// A Watchlist import's column check as the server describes it, in the shape
// the column dialog renders; null when the description is malformed.
export function watchlistColumnReview(payload) {
    if (typeof payload?.review_digest !== 'string' || !Array.isArray(payload.columns)) return null;
    return {
        destination: 'watchlist',
        reviewDigest: payload.review_digest,
        identifierChoices: payload.identifier_column ? [payload.identifier_column] : [],
        identifierLabel: payload.identifier_label || '',
        quantityChoices: [],
        matchingNote: payload.matching_note || '',
        notices: Array.isArray(payload.notices) ? payload.notices : [],
        selectionLimits: Array.isArray(payload.selection_limits) ? payload.selection_limits : [],
        columns: payload.columns.map((column) => ({
            index: column.index,
            name: column.name,
            label: column.label,
            role: column.role,
            examples: column.examples,
            selectable: column.selectable,
            checked: column.included_by_default,
            duplicateIdentifier: column.duplicate_identifier,
            reason: column.reason,
        })),
    };
}

export function initialColumnSelection(review) {
    return {
        identifier: review.identifierChoices.length === 1 ? review.identifierChoices[0] : '',
        quantity: review.quantityChoices.includes('quantity') ? 'quantity' : review.quantityChoices.length === 1 ? review.quantityChoices[0] : '',
        included: review.columns.filter((column) => column.checked).map((column) => column.index),
    };
}

export function columnIsIncluded(review, selection, column) {
    if (!column.selectable) return false;
    if (column.role === 'Identifier') return column.name === selection.identifier || (review.destination === 'portfolio' && column.name === 'ticker' && Boolean(selection.identifier) && selection.identifier !== 'ticker');
    if (column.role === 'Share quantity') return column.name === selection.quantity;
    return selection.included.includes(column.index);
}

export function columnSelectionError(review, selection) {
    if (!selection.identifier) return review.identifierChoices.length ? 'Choose the identifier to use for matching.' : 'No supported security identifier was found. Check the header row.';
    if (review.destination === 'portfolio' && !selection.quantity) return 'Actual Portfolio requires a Quantity column. A file without holdings may belong in Watchlist upload.';
    const included = review.columns.filter((column) => columnIsIncluded(review, selection, column));
    const exceeded = (review.selectionLimits ?? []).find(({ role, maximum }) => (
        included.filter((column) => column.role === role).length > maximum
    ));
    return exceeded?.message ?? '';
}

export function confirmedImport(review, selection) {
    const error = columnSelectionError(review, selection);
    if (error) throw new Error(error);
    const columns = review.columns.filter((column) => columnIsIncluded(review, selection, column));
    if (review.destination === 'watchlist') {
        // The server keeps these columns of its own copy of the table.
        return { review_digest: review.reviewDigest, included: columns.map((column) => column.index) };
    }
    return {
        identifierColumn: selection.identifier,
        matrix: review.matrix.map((row, rowIndex) => columns.map((column) => (
            rowIndex === review.headerIndex && column.role === 'Share quantity' ? 'quantity' : row[column.index] ?? ''
        ))),
    };
}
